import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/ToastContext';
import { fetchGuestRefunds, requestGuestRefund } from '../../api/guestApi';

const badgeStyles = {
  requested: 'bg-amber-100 text-amber-700',
  approved: 'bg-sky-100 text-sky-700',
  processing: 'bg-indigo-100 text-indigo-700',
  refunded: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  rejected: 'bg-slate-100 text-slate-700',
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || 'bg-slate-100 text-slate-700';
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${cls}`}>{label}</span>;
};

const formatDateTime = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatMoney = (currency, amountPaise, amountRupees) => {
  if (Number.isFinite(Number(amountPaise))) {
    const value = Number(amountPaise) / 100;
    return `${currency || 'INR'} ${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  if (Number.isFinite(Number(amountRupees))) {
    return `${currency || 'INR'} ${Number(amountRupees).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  return `${currency || ''}`.trim();
};

const resolveAmountPaise = (refund) => refund?.amountPaise ?? refund?.amount_paise ?? null;

const toPaise = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric * 100);
};

const GuestRefunds = () => {
  const toast = useToast();
  const [form, setForm] = useState({ paymentId: '', amount: '', reason: '' });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async (nextPage = page) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchGuestRefunds({ page: nextPage, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({ page: res.page || nextPage, limit: res.limit || pagination.limit, total: res.total || 0 });
    } catch (err) {
      setError(err.message || 'Unable to load refunds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const paymentId = form.paymentId.trim();
    const amountPaise = toPaise(form.amount);
    if (!paymentId) {
      setError('Payment ID is required.');
      return;
    }
    if (!amountPaise) {
      setError('Enter a valid refund amount.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await requestGuestRefund({
        paymentId,
        amount: amountPaise,
        reason: form.reason.trim(),
      });
      toast.success('Refund request submitted');
      setForm({ paymentId: '', amount: '', reason: '' });
      await load(1);
      setPage(1);
    } catch (err) {
      setError(err.message || 'Refund request failed');
      toast.error(err.message || 'Refund request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const pageSummary = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total || 0);
    if (!pagination.total) return '0 results';
    return `${start}-${end} of ${pagination.total}`;
  }, [pagination]);

  const timelineSteps = ['requested', 'approved', 'processing', 'refunded'];

  const renderTimeline = (refund) => {
    const status = String(refund.status || '').toLowerCase();
    if (!timelineSteps.includes(status)) {
      const reason = refund.rejectionReason || refund.rejection_reason || refund.failureReason || refund.failure_reason;
      return (
        <div className="mt-2 text-xs text-slate-500">
          Status: <span className="font-semibold text-slate-700">{status || 'unknown'}</span>
          {reason ? <div className="mt-1 text-rose-600">Reason: {reason}</div> : null}
        </div>
      );
    }
    return (
      <ol className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        {timelineSteps.map((step) => {
          const isActive = step === status;
          const isComplete = timelineSteps.indexOf(step) <= timelineSteps.indexOf(status);
          const cls = isActive
            ? 'bg-emerald-600 text-white'
            : isComplete
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-slate-100 text-slate-500';
          return (
            <li key={step} className={`rounded-full px-3 py-1 font-semibold ${cls}`}>
              {step.replace(/_/g, ' ')}
            </li>
          );
        })}
      </ol>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Refunds</p>
          <h2 className="text-2xl font-semibold text-slate-900">Request and track refunds.</h2>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{pageSummary}</span>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900">Request a refund</h3>
        <p className="mt-1 text-xs text-slate-500">Provide the payment ID and amount in INR.</p>
        <form onSubmit={handleSubmit} className="mt-4 grid gap-3 md:grid-cols-3">
          <input
            type="text"
            placeholder="Payment ID"
            value={form.paymentId}
            onChange={(e) => setForm((prev) => ({ ...prev, paymentId: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Amount (INR)"
            value={form.amount}
            onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
          <input
            type="text"
            placeholder="Reason (optional)"
            value={form.reason}
            onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
          <div className="md:col-span-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? 'Submitting...' : 'Submit refund request'}
            </button>
          </div>
        </form>
        {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading && <div className="px-4 py-3 text-sm text-slate-500">Loading refunds...</div>}
        {!loading && !items.length && !error && (
          <div className="px-4 py-6 text-sm text-slate-500">No refunds found.</div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3">Refund</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((refund) => (
                  <tr key={refund.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {refund.refundRef || refund.refund_ref || `#${refund.id}`}
                      {renderTimeline(refund)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {refund.payment?.paymentRef || refund.paymentId}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {formatMoney(refund.currency, resolveAmountPaise(refund), refund.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge value={refund.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatDateTime(refund.createdAt || refund.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Previous
          </button>
          <div className="text-slate-600">
            Page {pagination.page} of {Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 10)))}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={pagination.page >= Math.ceil((pagination.total || 0) / (pagination.limit || 10))}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestRefunds;
